/** @jsxImportSource theme-ui */
import { Text, Box, Flex, Card, Spinner, Heading, Label } from 'theme-ui';
import { useEffect } from 'react';
import { useMachine } from '@xstate/react';
import { useParams } from 'react-router-dom';
import { useDialogState, Dialog, DialogBackdrop } from 'reakit/Dialog';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import CustomDatePicker from '@/components/CustomDatePicker';
import { GrayFormikField } from '@/components/GrayFormikField';
import { useEndCompanyLicensesMutation } from '@/store/apiService';
import { LicenseTerminationTiming } from '@/types/types';
import companyLicensesMachine, {
    getCompanyLicenses,
    getError,
    getIsLoading,
    LOAD_COMPANY_LICENSES,
} from './machines/company/companyLicenses';
import { CompanyLicensesPartial } from './companyLicensesPartial';

const SPECIFIC_DATE = 'SpecificDate';
type EndTiming = LicenseTerminationTiming | typeof SPECIFIC_DATE;

const END_TIMING_OPTIONS: { label: string; value: EndTiming }[] = [
    { label: 'Immediately', value: 'Immediately' },
    { label: 'At the end of the billing period', value: 'AtSubscriptionBillingPeriodEnd' },
    { label: 'At the end of the binding period', value: 'AtBindingPeriodEnd' },
    { label: 'On a specific date', value: SPECIFIC_DATE },
];

const pad = (n: number) => String(n).padStart(2, '0');
const toLocalDateTimeString = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;

const CompanyLicenses = () => {
    const { id } = useParams<{ id: string }>();
    const [state, send] = useMachine(companyLicensesMachine);
    const companyLicenses = getCompanyLicenses(state);
    const isLoading = getIsLoading(state);
    const error = getError(state);
    const endDialog = useDialogState();
    const [endCompanyLicenses, endCompanyLicensesState] = useEndCompanyLicensesMutation();

    useEffect(() => {
        if (id) {
            send({ type: LOAD_COMPANY_LICENSES, payload: { CompanyId: id } });
        }
    }, [id]);

    return (
        <Box>
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Heading>Company licenses</Heading>
                {id ? (
                    <Button variant="secondary" onClick={endDialog.show}>
                        End licenses
                    </Button>
                ) : null}
            </Flex>

            {isLoading ? <Spinner size={16} /> : null}
            {companyLicenses?.length > 0 ? (
                <CompanyLicensesPartial companyLicenses={companyLicenses} />
            ) : null}
            {error ? (
                <Card variant="widget">
                    <Text color="danger">{error}</Text>
                </Card>
            ) : null}

            <DialogBackdrop {...endDialog}>
                <Dialog
                    {...endDialog}
                    aria-label="End company licenses"
                    sx={{
                        '&&': {
                            padding: 4,
                            maxWidth: '480px',
                            width: '100%',
                        },
                    }}
                >
                    <Heading as="h3" mb={3}>
                        End company licenses
                    </Heading>

                    {id ? (
                        <Formik
                            initialValues={{
                                CompanyId: id,
                                Timing: 'Immediately' as EndTiming,
                                EndDate: null as string | null,
                                CloseCompany: false,
                            }}
                            validate={(values) =>
                                values.Timing === SPECIFIC_DATE && !values.EndDate
                                    ? { EndDate: 'Select an end date' }
                                    : {}
                            }
                            onSubmit={(values, { setSubmitting }) => {
                                const isSpecificDate = values.Timing === SPECIFIC_DATE;
                                endCompanyLicenses({
                                    CompanyId: values.CompanyId,
                                    EndDate: isSpecificDate ? values.EndDate : null,
                                    CloseCompany: values.CloseCompany,
                                    Type: isSpecificDate
                                        ? 'AtSubscriptionBillingPeriodEnd'
                                        : values.Timing,
                                })
                                    .unwrap()
                                    .then(() => {
                                        setSubmitting(false);
                                        endDialog.hide();
                                        toast('Licenses have been scheduled to end', {
                                            autoClose: 5000,
                                        });
                                        send({
                                            type: LOAD_COMPANY_LICENSES,
                                            payload: { CompanyId: id },
                                        });
                                    })
                                    .catch(() => {
                                        setSubmitting(false);
                                    });
                            }}
                        >
                            {(formProps) => {
                                const endDateValue = formProps.values.EndDate
                                    ? new Date(formProps.values.EndDate)
                                    : null;
                                const isSpecificDate = formProps.values.Timing === SPECIFIC_DATE;

                                return (
                                    <form onSubmit={formProps.handleSubmit}>
                                        <Box mb={3}>
                                            <Text as="p" sx={{ fontSize: 1, color: 'gray.6' }}>
                                                Company ID: {id}
                                            </Text>
                                        </Box>

                                        <GrayFormikField
                                            type="radio"
                                            name="Timing"
                                            label="When should the licenses end?"
                                            options={END_TIMING_OPTIONS}
                                        />

                                        {isSpecificDate ? (
                                            <Box mb={3}>
                                                <Label mb={1}>End date</Label>
                                                <CustomDatePicker
                                                    variation="normal"
                                                    field={{
                                                        name: 'EndDate',
                                                        value: endDateValue,
                                                    }}
                                                    onChange={(value: Date | null) => {
                                                        formProps.setFieldValue(
                                                            'EndDate',
                                                            value ? toLocalDateTimeString(value) : null
                                                        );
                                                    }}
                                                />
                                                {formProps.errors.EndDate ? (
                                                    <Text color="danger">
                                                        {formProps.errors.EndDate}
                                                    </Text>
                                                ) : null}
                                                <Text as="p" sx={{ fontSize: 1, color: 'gray.6' }}>
                                                    Licenses end on this date. A past date ends them
                                                    right away.
                                                </Text>
                                            </Box>
                                        ) : null}

                                        <GrayFormikField
                                            type="checkbox"
                                            name="CloseCompany"
                                            label="Also close the company"
                                        />

                                        <Flex sx={{ justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                onClick={endDialog.hide}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="danger"
                                                disabled={!formProps.isValid}
                                                isLoading={
                                                    formProps.isSubmitting ||
                                                    endCompanyLicensesState.isLoading
                                                }
                                            >
                                                End licenses
                                            </Button>
                                        </Flex>
                
                                    </form>
                                );
                            }}
                        </Formik>
                    ) : null}
                </Dialog>
            </DialogBackdrop>
        </Box>
    );
};

export default CompanyLicenses;
